import { useCallback, useEffect, useRef, useState } from 'react';
import { SEA } from '../config/gun.config';
import gun from '../config/gun.config';

export interface SEAPair {
  pub: string;
  priv: string;
  epub: string;
  epriv: string;
}

const STORAGE_KEY = 'stabeon_sea_pair';

/**
 * Encrypt the keypair using userId as the password key and persist to GUN.
 * Called after generating or loading a keypair so any device that knows the
 * nullifierHash can restore the same keypair.
 */
async function backupKeypairToGUN(pair: SEAPair, userId: string): Promise<void> {
  try {
    const sealed = await SEA.encrypt(JSON.stringify(pair), userId);
    if (sealed) {
      gun.get('stabeon_users').get(userId).get('sealed_kp').put(sealed as any);
    }
  } catch {
    // Best-effort — don't block normal flow
  }
}

/**
 * Try to restore a keypair from GUN using the userId as the decryption key.
 * Returns null if not found or decryption fails.
 */
export async function restoreKeypairFromGUN(userId: string): Promise<SEAPair | null> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(null), 4000);
    gun.get('stabeon_users').get(userId).get('sealed_kp').once(async (sealed: any) => {
      clearTimeout(timer);
      if (!sealed || typeof sealed !== 'string') { resolve(null); return; }
      try {
        const decrypted = await SEA.decrypt(sealed, userId);
        if (!decrypted) { resolve(null); return; }
        const kp: SEAPair = typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted as SEAPair;
        if (kp?.pub && kp?.epub) resolve(kp);
        else resolve(null);
      } catch {
        resolve(null);
      }
    });
  });
}

export function useSEA(userId: string | null) {
  const [pair, setPair] = useState<SEAPair | null>(null);
  const initRef = useRef(false);

  // Load or generate keypair
  useEffect(() => {
    if (!userId || initRef.current) return;
    initRef.current = true;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.pub && parsed.epub) {
          setPair(parsed);
          // Publish epub to user profile for others to derive shared secrets
          gun.get('stabeon_users').get(userId).get('epub').put(parsed.epub);
          // Re-upload backup in case GUN didn't have it yet (e.g. first run after update)
          backupKeypairToGUN(parsed, userId);
          return;
        }
      } catch { /* regenerate */ }
    }

    // Generate new keypair
    (async () => {
      const newPair = await SEA.pair() as SEAPair;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPair));
      setPair(newPair);
      // Publish public encryption key
      gun.get('stabeon_users').get(userId).get('epub').put(newPair.epub);
      // Backup encrypted keypair to GUN for cross-device recovery
      await backupKeypairToGUN(newPair, userId);
    })();
  }, [userId]);

  // Get shared secret for 1:1 encrypted communication
  const getSharedSecret = useCallback(async (theirEpub: string): Promise<string | null> => {
    if (!pair) return null;
    try {
      const secret = await SEA.secret(theirEpub, pair as any);
      return secret as string;
    } catch {
      return null;
    }
  }, [pair]);

  // Encrypt a message
  const encrypt = useCallback(async (plaintext: string, secret: string): Promise<string | null> => {
    try {
      const encrypted = await SEA.encrypt(plaintext, secret);
      return encrypted as string;
    } catch {
      return null;
    }
  }, []);

  // Decrypt a message
  const decrypt = useCallback(async (ciphertext: string, secret: string): Promise<string | null> => {
    try {
      const decrypted = await SEA.decrypt(ciphertext, secret);
      return decrypted as string;
    } catch {
      return null;
    }
  }, []);

  // Generate a random passphrase for group encryption
  const generateGroupKey = useCallback(async (): Promise<string> => {
    const random = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(random, b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  // Encrypt group key for a specific member (so only they can read it)
  const encryptGroupKeyForMember = useCallback(async (groupKey: string, memberEpub: string): Promise<string | null> => {
    if (!pair) return null;
    const secret = await SEA.secret(memberEpub, pair as any);
    if (!secret) return null;
    return await SEA.encrypt(groupKey, secret as string) as string;
  }, [pair]);

  // Decrypt a group key that was encrypted for us
  const decryptGroupKey = useCallback(async (encryptedGroupKey: string, senderEpub: string): Promise<string | null> => {
    if (!pair) return null;
    const secret = await SEA.secret(senderEpub, pair as any);
    if (!secret) return null;
    return await SEA.decrypt(encryptedGroupKey, secret as string) as string;
  }, [pair]);

  return {
    pair,
    epub: pair?.epub || null,
    getSharedSecret,
    encrypt,
    decrypt,
    generateGroupKey,
    encryptGroupKeyForMember,
    decryptGroupKey,
  };
}
